import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { Trophy, Star, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function NotificationCenter({ user }) {
    const [notifications, setNotifications] = useState([]);

    useEffect(() => {
        if (!user?.id) return;

        // Subscribe to new challenges
        const unsubscribeChallenge = base44.entities.Challenge.subscribe((event) => {
            if (event.type === 'create') {
                const challenge = event.data;
                const now = new Date();
                const startDate = new Date(challenge.start_at);
                const endDate = new Date(challenge.end_at);

                // Only notify if challenge is active now
                if (challenge.is_active && challenge.status === 'active' && startDate <= now && endDate > now) {
                    addNotification({
                        id: `challenge-${challenge.id}-${Date.now()}`,
                        type: 'challenge',
                        title: 'Nouveau Challenge !',
                        message: challenge.title,
                        icon: Trophy,
                        color: 'bg-[#6E2BFF]',
                        data: challenge
                    });

                    // Send push notification if permitted
                    if ('Notification' in window && Notification.permission === 'granted') {
                        new Notification('Nouveau Challenge ! 🏆', {
                            body: challenge.title,
                            icon: 'https://jszqmvsvkasqsnxkhqor.supabase.co/storage/v1/object/public/playground-photos/zno-icon.png',
                            tag: `challenge-${challenge.id}`
                        });
                        // Play notification sound
                        const audio = new Audio('data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEAQB8AAAB9AAACABAAZGF0YQIAAAAAAA==');
                        audio.play().catch(() => {});
                    }
                }
            }
        });

        // Subscribe to challenge draws
        const unsubscribeDraw = base44.entities.ChallengeDraw.subscribe((event) => {
            if (event.type === 'create') {
                const draw = event.data;
                
                // Notify if user is finalist or winner
                if (draw.finalists_user_ids?.includes(user.id) || draw.winner_user_id === user.id) {
                    const notifType = draw.winner_user_id === user.id ? 'win' : 'finalist';
                    const message = draw.winner_user_id === user.id 
                        ? 'Vous avez gagné le tirage !' 
                        : 'Vous êtes finaliste du tirage !';
                    
                    addNotification({
                        id: `draw-${draw.id}-${Date.now()}`,
                        type: 'draw',
                        title: message,
                        message: `Tirage: ${new Date(draw.draw_date).toLocaleDateString('fr-FR')}`,
                        icon: Star,
                        color: notifType === 'win' ? 'bg-yellow-500' : 'bg-blue-500',
                        data: draw
                    });
                }
            }
        });

        return () => {
            unsubscribeChallenge();
            unsubscribeDraw();
        };
    }, [user?.id]);

    const addNotification = (notif) => {
        setNotifications(prev => [notif, ...prev]);
        
        // Auto-dismiss after 8 seconds
        const timer = setTimeout(() => {
            removeNotification(notif.id);
        }, 8000);

        return () => clearTimeout(timer);
    };

    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    return (
        <div className="fixed top-20 left-4 md:left-auto md:right-4 z-50 space-y-2 max-w-sm w-full md:w-80 pointer-events-none">
            <AnimatePresence>
                {notifications.map((notif) => {
                    const Icon = notif.icon;
                    return (
                        <motion.div
                            key={notif.id}
                            initial={{ opacity: 0, x: -100, scale: 0.9 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, x: -100, scale: 0.9 }}
                            transition={{ duration: 0.3 }}
                            className={`${notif.color} text-white p-4 rounded-lg shadow-2xl pointer-events-auto cursor-pointer hover:scale-105 transition-transform`}
                            onClick={() => {
                                removeNotification(notif.id);
                                if (window.__onChallengesClick) window.__onChallengesClick();
                            }}
                        >
                            <div className="flex items-start gap-3">
                                <div className="bg-white/20 rounded-full p-2 flex-shrink-0">
                                    <Icon className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-sm">{notif.title}</p>
                                    <p className="text-xs opacity-90 mt-1 line-clamp-2">{notif.message}</p>
                                </div>
                                <button
                                    onClick={() => removeNotification(notif.id)}
                                    className="text-white/80 hover:text-white transition-colors flex-shrink-0 mt-0.5"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
}